class Utils {
  /**
   * Returns the first element that is a descendant of node that
   * matches selectors.
   *
   * @param {string} selector - A DOMString containing one or more selectors to match. This string
   *   must be a valid CSS selector string
   * @param {Object} [config] - You can pass a context to scope the element selection
   * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element
   *   in
   *
   * @returns {Element|null} The found element or null
   */
  static getElement (selector, { context = document } = {}) {
    return context.querySelector(selector)
  }

  /**
   * Returns a list of elements that are a descendant of node that
   * matches selectors.
   *
   * @param {string} selector - A DOMString containing one or more selectors to match. This string
   *   must be a valid CSS selector string
   * @param {Object} [config] - You can pass a context to scope the element selection
   * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element
   *   in
   * @param {boolean} [asArray=true] - Return the elements as an Array instead of an NodeList?
   *
   * @returns {Array<Element>|NodeListOf<Element>} The found elements or null
   */
  static getElements (selector, { context = document, asArray = true } = {}) {
    const items = context.querySelectorAll(selector)

    return asArray ? Array.from(items) : items
  }

  /**
   * Checks if the passed string contains any items in an array
   *
   * @param {string} str - The string we will be searching
   * @param {Array} [items] - A list of the items we will be checking for presence in the str
   *
   * @returns {boolean}
   */
  static doesStringContainAny (str, items = []) {
    return items.some(i => str.includes(i))
  }

  /**
   * Creates an HTMLElement
   *
   * @param {string} tag - What type of element we want this to be
   * @param {Object} [options] - A key / value pair of properties and values we want to set on the
   *   newly created element
   *
   * @returns {HTMLElement}
   */
  static createElement (tag, options = {}) {
    const el = document.createElement(tag)

    Object.entries(options).forEach(([key, value]) => {
      el.setAttribute(key, value)
    })

    return el
  }

  /**
   * Creates an array starting from zero with the length of the passed amount
   *
   * @param {number} amount - How long do we want this array to be?
   *
   * @returns {Array}
   */
  static range (amount) {
    return [...Array(amount).keys()]
  }

  /**
   * camelCase a string
   *
   * @param {string} str
   *
   * @return {string} value - formatted value
   */
  static camelize (str) {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase()
      })
      .replace(/\s+/g, '')
  }

  /**
   * A Javascript equivalent of Shopify's | img_url filter
   *
   * @param {Object} The properties we will use to create the URL
   * @param {string} src - The Shopify CDN URL of the image
   * @param {string} size - What size we want the image to be
   * @param {null|String} [crop=null] - By default the images aren't cropped but we can choose to
   *   crop them
   * @param {null|Number} [scale=null] - Images are 1x by default, you can choose to pass a
   *   different scale
   *
   * @returns {string}
   */
  static resizeImage ({ src, size, crop = null, scale = null } = {}) {
    if (size == null) {
      return src
    }

    if (size === 'master') {
      return src.replace(/http(s)?:/, '')
    }

    const match = src.match(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?/i,
    )

    if (match != null) {
      const prefix = src.split(match[0])
      const suffix = match[0]
      const scaleString = scale ? `@${scale}x` : ''
      const cropString = crop ? `_crop_${crop}` : ''

      return `${prefix[0]}_${size}${cropString}${scaleString}${suffix}`.replace(
        /http(s)?:/,
        '',
      )
    } else {
      return null
    }
  }

  /**
   * This is the default config we will use when using fetch()
   *
   * @param {string} type
   *
   * @returns {Object}
   */
  static getDefaultRequestConfig (type = 'json') {
    return {
      method: 'POST',
      headers: {
        Accept: `application/${type}`,
        'Content-Type': 'application/json',
      },
    }
  }

  /**
   * Default fetch boilerplate
   * @param {string} url - The URL we are looking to fetch
   * @param {Object} [config] - .fetch config to override the default
   *
   * @returns {Promise}
   */
  static fetchJSON (url, config = {}) {
    return fetch(
      url,
      Object.assign({}, Utils.getDefaultRequestConfig(), config),
    ).then(response => {
      if (!response.ok) {
        throw response
      }
      return response.json()
    })
  }

  /**
   * This will fetch product data from Shopify's AJAX API by handle
   *
   * @param {string} handle - The products handle
   *
   * @returns {Promise}
   */
  static fetchProductData (handle) {
    try {
      return Utils.fetchJSON(`/products/${handle}.js`)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Gets the offsetTop of the passed element relevant to it's offsetParent
   *
   * @param {HTMLElement|Element} el - The element we'll be getting the offsetTop for
   * @param {Document|HTMLElement} [parent=HTMLBodyElement] - The offsetParent we want to get the
   *   offsetTop relative to
   * @param {number} [offsetTop=0] - The starting offsetTop. This tends to be 0 but given this is a
   *   recursive function, we need to pass it to let it build up
   *
   * @returns {number}
   */
  static getOffsetTop (el, parent = document.body, offsetTop = 0) {
    if (!isNaN(el.offsetTop)) {
      offsetTop += el.offsetTop
    }

    return el.offsetParent === parent
      ? offsetTop
      : Utils.getOffsetTop(el.offsetParent, parent, offsetTop)
  }

  /**
   * Format money values based on your shop currency settings
   * @param  {number|string} cents - value in cents or dollar amount e.g. 300 cents
   * or 3.00 dollars
   * @param  {string} [format] - shop money_format setting
   *
   * @return {string} value - formatted value
   */
  static formatMoney (cents, format = window.theme.moneyFormat) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '')
    }
    let value = ''
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    const formatString = format

    function formatWithDelimiters (
      number,
      precision = 2,
      thousands = ',',
      decimal = '.',
    ) {
      if (isNaN(number) || number == null) {
        return 0
      }

      number = (number / 100.0).toFixed(precision)

      const parts = number.split('.')
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`,
      )
      const centsAmount = parts[1] ? decimal + parts[1] : ''

      return dollarsAmount + centsAmount
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2)
        break
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0)
        break
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',')
        break
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',')
        break
    }

    return formatString.replace(placeholderRegex, value)
  }

  static getOrdinalForDay (day) {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  /**
   * Inject variables into a translated string.
   *
   * @param {string} translation
   * @param {{ [variable: string]: any }} variables
   * @param {{ delimiters?: [string, string][] }} [options]
   *
   * @example
   * // returns Hurry! Only 2 left in stock.
   * translateString('Hurry! Only {{ count }} left in stock.', { count: 2 })
   */
  static translateString (
    translation,
    variables,
    options = {
      delimiters: [
        ['{{\\s*', '\\s*}}'],
        ['%{', '}'],
      ],
    },
  ) {
    return Object.entries(variables).reduce((baseTranslation, [key, value]) => {
      const replacePattern = options.delimiters
        .map(([start, end]) => `${start}${key}${end}`)
        .join('|')

      return baseTranslation.replace(new RegExp(replacePattern, 'g'), value)
    }, translation)
  }

  /**
   * @param {Function} fn
   * @param {number} wait
   *
   * @return {Function}
   */
  static debounce (fn, wait) {
    let t

    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  /**
   * @param {FormData} formData
   *
   * @return {Object}
   */
  static buildProperties (formData) {
    const propertyKeyValues = [...formData.entries()].filter(
      ([key, value]) => key.includes('properties[') && value !== '',
    )

    /**
     * @param {Object} properties
     * @param {[string, string]} property
     *
     * @returns {Object}
     */
    function propertyReducer (properties, [key, value]) {
      const strippedKey = key.replace('properties[', '').slice(0, -1)

      return {
        ...properties,
        [strippedKey]: value,
      }
    }

    return propertyKeyValues.reduce(
      (properties, property) => propertyReducer(properties, property),
      {},
    )
  }

  /**
   * Returns a normalised string using Canonical Decomposition (NFD)
   */
  static normalize (value) {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
  }

  /**
   * Returns the current viewport size.
   * @return {'xs'|'sm'|'md'|'lg'|'xl'}
   */
  static viewportSize () {
    const breakpoints = {
      xs: window.matchMedia('(max-width: 767px)').matches,
      sm: window.matchMedia('(min-width: 768px) and (max-width: 900px)')
        .matches,
      md: window.matchMedia('(min-width: 901px) and (max-width: 1200px)')
        .matches,
      lg: window.matchMedia('(min-width: 1201px) and (max-width: 1439px)')
        .matches,
      xl: window.matchMedia('(min-width: 1440px)').matches,
    }
    return Object.keys(breakpoints).find(key => breakpoints[key])
  }

  /**
   * @returns {Boolean}
   */
  static prefersReducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /**
   * Returns array of shipping countries.
   */

  static shippingCountries () {
    return [
      { name: 'Åland Islands', site: 'EU', currency: 'EUR', code: 'AX' },
      { name: 'Albania', site: 'US', currency: 'USD', code: 'AL' },
      { name: 'Algeria', site: 'US', currency: 'USD', code: 'DZ' },
      { name: 'American Samoa', site: 'US', currency: 'USD', code: 'AS' },
      { name: 'Andorra', site: 'EU', currency: 'EUR', code: 'AD' },
      { name: 'Angola', site: 'US', currency: 'USD', code: 'AO' },
      { name: 'Anguilla', site: 'US', currency: 'USD', code: 'AI' },
      { name: 'Antigua and Barbuda', site: 'US', currency: 'USD', code: 'AG' },
      { name: 'Argentina', site: 'US', currency: 'USD', code: 'AR' },
      { name: 'Aruba', site: 'US', currency: 'USD', code: 'AW' },
      { name: 'Australia', site: 'EU', currency: 'EUR', code: 'AU' },
      { name: 'Austria', site: 'EU', currency: 'EUR', code: 'AT' },
      { name: 'Azerbaijan', site: 'US', currency: 'USD', code: 'AZ' },
      { name: 'Bahamas', site: 'US', currency: 'USD', code: 'BS' },
      { name: 'Bahrain', site: 'US', currency: 'USD', code: 'BH' },
      { name: 'Bangladesh', site: 'US', currency: 'USD', code: 'BD' },
      { name: 'Barbados', site: 'US', currency: 'USD', code: 'BB' },
      { name: 'Belgium', site: 'EU', currency: 'EUR', code: 'BE' },
      { name: 'Belize', site: 'US', currency: 'USD', code: 'BZ' },
      { name: 'Benin', site: 'US', currency: 'USD', code: 'BJ' },
      { name: 'Bermuda', site: 'US', currency: 'USD', code: 'BM' },
      { name: 'Bhutan', site: 'US', currency: 'USD', code: 'BT' },
      {
        name: 'Bolivia, Plurinational State of',
        site: 'US',
        currency: 'USD',
        code: 'BO',
      },
      {
        name: 'Bonaire, Sint Eustatius and Saba',
        site: 'US',
        currency: 'USD',
        code: 'BQ',
      },
      {
        name: 'Bosnia and Herzegovina',
        site: 'EU',
        currency: 'EUR',
        code: 'BA',
      },
      { name: 'Botswana', site: 'US', currency: 'USD', code: 'BW' },
      { name: 'Bouvet Island', site: 'US', currency: 'USD', code: 'BV' },
      { name: 'Brazil', site: 'US', currency: 'USD', code: 'BR' },
      {
        name: 'British Indian Ocean Territory',
        site: 'US',
        currency: 'USD',
        code: 'IO',
      },
      { name: 'Brunei Darussalam', site: 'US', currency: 'USD', code: 'BN' },
      { name: 'Bulgaria', site: 'EU', currency: 'EUR', code: 'BG' },
      { name: 'Burkina Faso', site: 'US', currency: 'USD', code: 'BF' },
      { name: 'Burundi', site: 'US', currency: 'USD', code: 'BI' },
      { name: 'Cambodia', site: 'US', currency: 'USD', code: 'KH' },
      { name: 'Cameroon', site: 'US', currency: 'USD', code: 'CM' },
      { name: 'Canada', site: 'US', currency: 'CAD', code: 'CA' },
      { name: 'Cape Verde', site: 'US', currency: 'USD', code: 'CV' },
      { name: 'Cayman Islands', site: 'US', currency: 'USD', code: 'KY' },
      {
        name: 'Central African Republic',
        site: 'US',
        currency: 'USD',
        code: 'CF',
      },
      { name: 'Chad', site: 'US', currency: 'USD', code: 'TD' },
      { name: 'Chile', site: 'US', currency: 'USD', code: 'CL' },
      { name: 'China', site: 'US', currency: 'USD', code: 'CN' },
      { name: 'Christmas Island', site: 'US', currency: 'USD', code: 'CX' },
      {
        name: 'Cocos (Keeling) Islands',
        site: 'US',
        currency: 'USD',
        code: 'CC',
      },
      { name: 'Colombia', site: 'US', currency: 'USD', code: 'CO' },
      { name: 'Comoros', site: 'US', currency: 'USD', code: 'KM' },
      { name: 'Congo', site: 'US', currency: 'USD', code: 'CG' },
      {
        name: 'Congo, the Democratic Republic of the',
        site: 'US',
        currency: 'USD',
        code: 'CD',
      },
      { name: 'Cook Islands', site: 'US', currency: 'USD', code: 'CK' },
      { name: 'Costa Rica', site: 'US', currency: 'USD', code: 'CR' },
      { name: "Côte d'Ivoire", site: 'US', currency: 'USD', code: 'CI' },
      { name: 'Croatia', site: 'EU', currency: 'EUR', code: 'HR' },
      { name: 'Cuba', site: 'US', currency: 'USD', code: 'CU' },
      { name: 'Curaçao', site: 'US', currency: 'USD', code: 'CW' },
      { name: 'Cyprus', site: 'EU', currency: 'EUR', code: 'CY' },
      { name: 'Czech Republic', site: 'EU', currency: 'EUR', code: 'CZ' },
      { name: 'Denmark', site: 'EU', currency: 'EUR', code: 'DK' },
      { name: 'Djibouti', site: 'US', currency: 'USD', code: 'DJ' },
      { name: 'Dominica', site: 'US', currency: 'USD', code: 'DM' },
      { name: 'Dominican Republic', site: 'US', currency: 'USD', code: 'DO' },
      { name: 'Ecuador', site: 'US', currency: 'USD', code: 'EC' },
      { name: 'Egypt', site: 'US', currency: 'USD', code: 'EG' },
      { name: 'El Salvador', site: 'US', currency: 'USD', code: 'SV' },
      { name: 'Equatorial Guinea', site: 'US', currency: 'USD', code: 'GQ' },
      { name: 'Eritrea', site: 'US', currency: 'USD', code: 'ER' },
      { name: 'Estonia', site: 'EU', currency: 'EUR', code: 'EE' },
      { name: 'Ethiopia', site: 'US', currency: 'USD', code: 'ET' },
      {
        name: 'Falkland Islands (Malvinas)',
        site: 'US',
        currency: 'USD',
        code: 'FK',
      },
      { name: 'Faroe Islands', site: 'EU', currency: 'EUR', code: 'FO' },
      { name: 'Fiji', site: 'US', currency: 'USD', code: 'FJ' },
      { name: 'Finland', site: 'EU', currency: 'EUR', code: 'FI' },
      {
        name: 'France',
        site: 'EU',
        currency: 'EUR',
        code: 'FR',
        markets: ['/en-fr', 'fr-fr'],
        languages: {
          en: 'en-fr',
          fr: 'fr-fr',
        },
      },
      { name: 'French Guiana', site: 'US', currency: 'USD', code: 'GF' },
      { name: 'French Polynesia', site: 'US', currency: 'USD', code: 'PF' },
      {
        name: 'French Southern Territories',
        site: 'US',
        currency: 'USD',
        code: 'TF',
      },
      { name: 'Gabon', site: 'US', currency: 'USD', code: 'GA' },
      { name: 'Gambia', site: 'US', currency: 'USD', code: 'GM' },
      { name: 'Georgia', site: 'EU', currency: 'EUR', code: 'GE' },
      { name: 'Germany', site: 'EU', currency: 'EUR', code: 'DE' },
      { name: 'Ghana', site: 'US', currency: 'USD', code: 'GH' },
      { name: 'Gibraltar', site: 'EU', currency: 'EUR', code: 'GI' },
      { name: 'Greece', site: 'EU', currency: 'EUR', code: 'GR' },
      { name: 'Greenland', site: 'US', currency: 'USD', code: 'GL' },
      { name: 'Grenada', site: 'US', currency: 'USD', code: 'GD' },
      { name: 'Guadeloupe', site: 'EU', currency: 'EUR', code: 'GP' },
      { name: 'Guam', site: 'US', currency: 'USD', code: 'GU' },
      { name: 'Guatemala', site: 'US', currency: 'USD', code: 'GT' },
      { name: 'Guernsey', site: 'EU', currency: 'EUR', code: 'GG' },
      { name: 'Guinea', site: 'US', currency: 'USD', code: 'GN' },
      { name: 'Guinea-Bissau', site: 'US', currency: 'USD', code: 'GW' },
      { name: 'Guyana', site: 'US', currency: 'USD', code: 'GY' },
      { name: 'Haiti', site: 'US', currency: 'USD', code: 'HT' },
      {
        name: 'Heard Island and McDonald Islands',
        site: 'US',
        currency: 'USD',
        code: 'HM',
      },
      {
        name: 'Holy See (Vatican City State)',
        site: 'EU',
        currency: 'EUR',
        code: 'VA',
      },
      { name: 'Honduras', site: 'US', currency: 'USD', code: 'HN' },
      { name: 'Hong Kong', site: 'US', currency: 'USD', code: 'HK' },
      { name: 'Hungary', site: 'EU', currency: 'EUR', code: 'HU' },
      { name: 'Iceland', site: 'EU', currency: 'EUR', code: 'IS' },
      { name: 'India', site: 'US', currency: 'USD', code: 'IN' },
      { name: 'Indonesia', site: 'US', currency: 'USD', code: 'ID' },
      {
        name: 'Iran, Islamic Republic of',
        site: 'US',
        currency: 'USD',
        code: 'IR',
      },
      { name: 'Iraq', site: 'US', currency: 'USD', code: 'IQ' },
      { name: 'Ireland', site: 'EU', currency: 'EUR', code: 'IE' },
      { name: 'Isle of Man', site: 'EU', currency: 'EUR', code: 'IM' },
      { name: 'Israel', site: 'US', currency: 'USD', code: 'IL' },
      {
        name: 'Italy',
        site: 'EU',
        currency: 'EUR',
        code: 'IT',
        markets: ['/en-it', 'it-it'],
        languages: {
          en: 'en-it',
          it: 'it-it',
        },
      },
      { name: 'Jamaica', site: 'US', currency: 'USD', code: 'JM' },
      { name: 'Japan', site: 'US', currency: 'USD', code: 'JP' },
      { name: 'Jersey', site: 'US', currency: 'USD', code: 'JE' },
      { name: 'Jordan', site: 'US', currency: 'USD', code: 'JO' },
      { name: 'Kazakhstan', site: 'US', currency: 'USD', code: 'KZ' },
      { name: 'Kenya', site: 'US', currency: 'USD', code: 'KE' },
      { name: 'Kiribati', site: 'US', currency: 'USD', code: 'KI' },
      {
        name: "Korea, Democratic People's Republic of",
        site: 'US',
        currency: 'USD',
        code: 'KP',
      },
      { name: 'Korea, Republic of', site: 'US', currency: 'USD', code: 'KR' },
      { name: 'Kuwait', site: 'US', currency: 'USD', code: 'KW' },
      { name: 'Kyrgyzstan', site: 'US', currency: 'USD', code: 'KG' },
      {
        name: "Lao People's Democratic Republic",
        site: 'US',
        currency: 'USD',
        code: 'LA',
      },
      { name: 'Latvia', site: 'EU', currency: 'EUR', code: 'LV' },
      { name: 'Lebanon', site: 'US', currency: 'USD', code: 'LB' },
      { name: 'Lesotho', site: 'US', currency: 'USD', code: 'LS' },
      { name: 'Liberia', site: 'US', currency: 'USD', code: 'LR' },
      { name: 'Libya', site: 'US', currency: 'USD', code: 'LY' },
      { name: 'Liechtenstein', site: 'EU', currency: 'EUR', code: 'LI' },
      { name: 'Lithuania', site: 'EU', currency: 'EUR', code: 'LT' },
      { name: 'Luxembourg', site: 'EU', currency: 'EUR', code: 'LU' },
      { name: 'Macao', site: 'US', currency: 'USD', code: 'MO' },
      {
        name: 'Macedonia, the former Yugoslav Republic of',
        site: 'EU',
        currency: 'EUR',
        code: 'MK',
      },
      { name: 'Madagascar', site: 'US', currency: 'USD', code: 'MG' },
      { name: 'Malawi', site: 'US', currency: 'USD', code: 'MW' },
      { name: 'Malaysia', site: 'US', currency: 'USD', code: 'MY' },
      { name: 'Maldives', site: 'US', currency: 'USD', code: 'MV' },
      { name: 'Mali', site: 'US', currency: 'USD', code: 'ML' },
      { name: 'Malta', site: 'EU', currency: 'EUR', code: 'MT' },
      { name: 'Marshall Islands', site: 'US', currency: 'USD', code: 'MH' },
      { name: 'Martinique', site: 'EU', currency: 'EUR', code: 'MQ' },
      { name: 'Mauritania', site: 'US', currency: 'USD', code: 'MR' },
      { name: 'Mauritius', site: 'US', currency: 'USD', code: 'MU' },
      { name: 'Mayotte', site: 'EU', currency: 'EUR', code: 'YT' },
      { name: 'Mexico', site: 'US', currency: 'USD', code: 'MX' },
      {
        name: 'Micronesia, Federated States of',
        site: 'US',
        currency: 'USD',
        code: 'FM',
      },
      { name: 'Moldova, Republic of', site: 'EU', currency: 'EUR', code: 'MD' },
      { name: 'Monaco', site: 'EU', currency: 'EUR', code: 'MC' },
      { name: 'Mongolia', site: 'US', currency: 'USD', code: 'MN' },
      { name: 'Montenegro', site: 'EU', currency: 'EUR', code: 'ME' },
      { name: 'Montserrat', site: 'US', currency: 'USD', code: 'MS' },
      { name: 'Morocco', site: 'US', currency: 'USD', code: 'MA' },
      { name: 'Mozambique', site: 'US', currency: 'USD', code: 'MZ' },
      { name: 'Myanmar', site: 'US', currency: 'USD', code: 'MM' },
      { name: 'Namibia', site: 'US', currency: 'USD', code: 'NA' },
      { name: 'Nauru', site: 'US', currency: 'USD', code: 'NR' },
      { name: 'Nepal', site: 'US', currency: 'USD', code: 'NP' },
      { name: 'Netherlands', site: 'EU', currency: 'EUR', code: 'NL' },
      { name: 'New Caledonia', site: 'US', currency: 'USD', code: 'NC' },
      { name: 'New Zealand', site: 'US', currency: 'USD', code: 'NZ' },
      { name: 'Nicaragua', site: 'US', currency: 'USD', code: 'NI' },
      { name: 'Niger', site: 'US', currency: 'USD', code: 'NE' },
      { name: 'Nigeria', site: 'US', currency: 'USD', code: 'NG' },
      { name: 'Niue', site: 'US', currency: 'USD', code: 'NU' },
      { name: 'Norfolk Island', site: 'US', currency: 'USD', code: 'NF' },
      {
        name: 'Northern Mariana Islands',
        site: 'US',
        currency: 'USD',
        code: 'MP',
      },
      { name: 'Norway', site: 'EU', currency: 'EUR', code: 'NO' },
      { name: 'Oman', site: 'US', currency: 'USD', code: 'OM' },
      { name: 'Pakistan', site: 'US', currency: 'USD', code: 'PK' },
      { name: 'Palau', site: 'US', currency: 'USD', code: 'PW' },
      {
        name: 'Palestinian Territory, Occupied',
        site: 'US',
        currency: 'USD',
        code: 'PS',
      },
      { name: 'Panama', site: 'US', currency: 'USD', code: 'PA' },
      { name: 'Papua New Guinea', site: 'US', currency: 'USD', code: 'PG' },
      { name: 'Paraguay', site: 'US', currency: 'USD', code: 'PY' },
      { name: 'Peru', site: 'US', currency: 'USD', code: 'PE' },
      { name: 'Philippines', site: 'US', currency: 'USD', code: 'PH' },
      { name: 'Pitcairn', site: 'US', currency: 'USD', code: 'PN' },
      { name: 'Poland', site: 'EU', currency: 'EUR', code: 'PL' },
      { name: 'Portugal', site: 'EU', currency: 'EUR', code: 'PT' },
      { name: 'Puerto Rico', site: 'US', currency: 'USD', code: 'PR' },
      { name: 'Qatar', site: 'US', currency: 'USD', code: 'QA' },
      { name: 'Réunion', site: 'EU', currency: 'EUR', code: 'RE' },
      { name: 'Romania', site: 'EU', currency: 'EUR', code: 'RO' },
      { name: 'Rwanda', site: 'US', currency: 'USD', code: 'RW' },
      { name: 'Saint Barthélemy', site: 'US', currency: 'USD', code: 'BL' },
      {
        name: 'Saint Helena, Ascension and Tristan da Cunha',
        site: 'US',
        currency: 'USD',
        code: 'SH',
      },
      {
        name: 'Saint Kitts and Nevis',
        site: 'US',
        currency: 'USD',
        code: 'KN',
      },
      { name: 'Saint Lucia', site: 'US', currency: 'USD', code: 'LC' },
      {
        name: 'Saint Martin (French part)',
        site: 'US',
        currency: 'USD',
        code: 'MF',
      },
      {
        name: 'Saint Pierre and Miquelon',
        site: 'US',
        currency: 'USD',
        code: 'PM',
      },
      {
        name: 'Saint Vincent and the Grenadines',
        site: 'US',
        currency: 'USD',
        code: 'VC',
      },
      { name: 'Samoa', site: 'US', currency: 'USD', code: 'WS' },
      { name: 'San Marino', site: 'EU', currency: 'EUR', code: 'SM' },
      {
        name: 'Sao Tome and Principe',
        site: 'US',
        currency: 'USD',
        code: 'ST',
      },
      { name: 'Saudi Arabia', site: 'US', currency: 'SAR', code: 'SA' },
      { name: 'Senegal', site: 'US', currency: 'USD', code: 'SN' },
      { name: 'Serbia', site: 'EU', currency: 'EUR', code: 'RS' },
      { name: 'Seychelles', site: 'US', currency: 'USD', code: 'SC' },
      { name: 'Sierra Leone', site: 'US', currency: 'USD', code: 'SL' },
      { name: 'Singapore', site: 'US', currency: 'USD', code: 'SG' },
      {
        name: 'Sint Maarten (Dutch part)',
        site: 'US',
        currency: 'USD',
        code: 'SX',
      },
      { name: 'Slovakia', site: 'EU', currency: 'EUR', code: 'SK' },
      { name: 'Slovenia', site: 'EU', currency: 'EUR', code: 'SI' },
      { name: 'Solomon Islands', site: 'US', currency: 'USD', code: 'SB' },
      { name: 'Somalia', site: 'US', currency: 'USD', code: 'SO' },
      { name: 'South Africa', site: 'US', currency: 'USD', code: 'ZA' },
      {
        name: 'South Georgia and the South Sandwich Islands',
        site: 'US',
        currency: 'USD',
        code: 'GS',
      },
      { name: 'South Sudan', site: 'US', currency: 'USD', code: 'SS' },
      { name: 'Spain', site: 'EU', currency: 'EUR', code: 'ES' },
      { name: 'Sri Lanka', site: 'US', currency: 'USD', code: 'LK' },
      { name: 'Sudan', site: 'US', currency: 'USD', code: 'SD' },
      { name: 'Suriname', site: 'US', currency: 'USD', code: 'SR' },
      {
        name: 'Svalbard and Jan Mayen',
        site: 'EU',
        currency: 'EUR',
        code: 'SJ',
      },
      { name: 'Swaziland', site: 'US', currency: 'USD', code: 'SZ' },
      { name: 'Sweden', site: 'EU', currency: 'EUR', code: 'SE' },
      { name: 'Switzerland', site: 'EU', currency: 'CHF', code: 'CH' },
      { name: 'Syrian Arab Republic', site: 'US', currency: 'USD', code: 'SY' },
      {
        name: 'Taiwan, Province of China',
        site: 'US',
        currency: 'USD',
        code: 'TW',
      },
      { name: 'Tajikistan', site: 'US', currency: 'USD', code: 'TJ' },
      {
        name: 'Tanzania, United Republic of',
        site: 'US',
        currency: 'USD',
        code: 'TZ',
      },
      { name: 'Thailand', site: 'US', currency: 'USD', code: 'TH' },
      { name: 'Timor-Leste', site: 'US', currency: 'USD', code: 'TL' },
      { name: 'Togo', site: 'US', currency: 'USD', code: 'TG' },
      { name: 'Tokelau', site: 'US', currency: 'USD', code: 'TK' },
      { name: 'Tonga', site: 'US', currency: 'USD', code: 'TO' },
      { name: 'Trinidad and Tobago', site: 'US', currency: 'USD', code: 'TT' },
      { name: 'Tunisia', site: 'US', currency: 'USD', code: 'TN' },
      { name: 'Turkey', site: 'US', currency: 'USD', code: 'TR' },
      { name: 'Turkmenistan', site: 'US', currency: 'USD', code: 'TM' },
      {
        name: 'Turks and Caicos Islands',
        site: 'US',
        currency: 'USD',
        code: 'TC',
      },
      { name: 'Tuvalu', site: 'US', currency: 'USD', code: 'TV' },
      { name: 'Uganda', site: 'US', currency: 'USD', code: 'UG' },
      { name: 'United Arab Emirates', site: 'US', currency: 'AED', code: 'AE' },
      { name: 'United Kingdom', site: 'EU', currency: 'GBP', code: 'GB' },
      { name: 'United States', site: 'US', currency: 'USD', code: 'US' },
      {
        name: 'United States Minor Outlying Islands',
        site: 'US',
        currency: 'USD',
        code: 'UM',
      },
      { name: 'Uruguay', site: 'US', currency: 'USD', code: 'UY' },
      { name: 'Uzbekistan', site: 'US', currency: 'USD', code: 'UZ' },
      { name: 'Vanuatu', site: 'US', currency: 'USD', code: 'VU' },
      {
        name: 'Venezuela, Bolivarian Republic of',
        site: 'US',
        currency: 'USD',
        code: 'VE',
      },
      { name: 'Viet Nam', site: 'US', currency: 'USD', code: 'VN' },
      {
        name: 'Virgin Islands, British',
        site: 'US',
        currency: 'USD',
        code: 'VG',
      },
      { name: 'Virgin Islands, U.S.', site: 'US', currency: 'USD', code: 'VI' },
      { name: 'Wallis and Futuna', site: 'US', currency: 'USD', code: 'WF' },
      { name: 'Western Sahara', site: 'US', currency: 'USD', code: 'EH' },
      { name: 'Yemen', site: 'US', currency: 'USD', code: 'YE' },
      { name: 'Zambia', site: 'US', currency: 'USD', code: 'ZM' },
      { name: 'Zimbabwe', site: 'US', currency: 'USD', code: 'ZW' },
    ]
  }

  /**
   * Sets a cookie.
   */
  static setCookie (cname, cvalue, exdays) {
    const d = new Date()
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000)
    const expires = `expires=${d.toUTCString()}`
    document.cookie = `${cname}=${cvalue};${expires};path=/`
  }

  /**
   * Sets a cookie.
   */

  static getCookie (cname) {
    const name = `${cname}=`
    const decodedCookie = decodeURIComponent(document.cookie)
    const ca = decodedCookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') {
        c = c.substring(1)
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length)
      }
    }
    return ''
  }

  static fetchBrowsingContext () {
    return Utils.fetchJSON(`/browsing_context_suggestions.json`, {
      method: 'GET',
    })
  }

  static isPackshotImage (src) {
    if (!src) return null

    const filename = src
      .split('/')
      .pop()
      .split('?')[0]
      .split('.')[0]
      .toLowerCase()

    return filename.endsWith('-packshot')
  }

  static getGenderFromFilename (src) {
    if (!src) return null

    const filename = src
      .split('/')
      .pop()
      .split('?')[0]
      .split('.')[0]
      .toLowerCase()

    const suffix = filename.slice(-2)

    if (suffix === '-m') return 'male'
    if (suffix === '-w') return 'female'

    return null
  }
}

window.BAO.Utils = Utils
